// docs/34 SX6 — hero voices on the client: the forge's choice, and the lines a hero speaks where only
// their own player is listening (the level-up card, the forge's preview). Mirrors `fk_sfx::cast`:
// a masculine voice speaks its class's lines in band `hero_id % 64` (wrapped to the bank's bands),
// a feminine voice the battle efforts; a hero with no `hero_voice` row alternates by id.
import { playSfx, type SfxKey } from './index';

export type HeroVoiceCode = 0 | 1;
export const HERO_VOICES = [{ value: 0, label: 'Masculine', title: 'Speaks the class lines and the male battle efforts' }, { value: 1, label: 'Feminine', title: 'Speaks the female battle efforts' }] as const;

/** `HeroVoice::default_for`: even hero ids feminine, odd masculine. */
export function heroVoiceOf(heroId: bigint, row?: { voice: number }): HeroVoiceCode {
  if (row) return row.voice === 1 ? 1 : 0;
  return heroId % 2n === 0n ? 1 : 0;
}

export type HeroLine = 'ready' | 'attack' | 'victory' | 'downed' | 'level' | 'item';

/** `Barks::bank`, as literals so the build bundles what the client speaks itself. */
const BARKS: Record<string, Record<HeroLine, SfxKey>> = {
  fighter: { ready: 'bark.fighter_ready', attack: 'bark.fighter_attack', victory: 'bark.fighter_victory', downed: 'bark.fighter_downed', level: 'bark.fighter_level', item: 'bark.fighter_item' },
  rogue: { ready: 'bark.rogue_ready', attack: 'bark.rogue_attack', victory: 'bark.rogue_victory', downed: 'bark.rogue_downed', level: 'bark.rogue_level', item: 'bark.rogue_item' },
  wizard: { ready: 'bark.wizard_ready', attack: 'bark.wizard_attack', victory: 'bark.wizard_victory', downed: 'bark.wizard_downed', level: 'bark.wizard_level', item: 'bark.wizard_item' },
  cleric: { ready: 'bark.cleric_ready', attack: 'bark.cleric_attack', victory: 'bark.cleric_victory', downed: 'bark.cleric_downed', level: 'bark.cleric_level', item: 'bark.cleric_item' },
  ranger: { ready: 'bark.ranger_ready', attack: 'bark.ranger_attack', victory: 'bark.ranger_victory', downed: 'bark.ranger_downed', level: 'bark.ranger_level', item: 'bark.ranger_item' },
};

export const barkKey = (classId: string, line: HeroLine): SfxKey | undefined => BARKS[classId]?.[line];

/** `species_pitch` (fk_sfx::cast), by `SpeciesId` index. */
const SPECIES_PITCH = [10_000, 9_200, 10_350, 11_000, 9_000, 9_600, 8_700, 10_150, 11_300, 10_800];
/** Spoken lines carry half the species' shift (`HeroCasting::line_pitch`). */
export const linePitch = (species: number): number => ((SPECIES_PITCH[species] ?? 10_000) + 10_000) / 20_000;
export const effortPitch = (species: number): number => (SPECIES_PITCH[species] ?? 10_000) / 10_000;

/**
 * A hero's own line where only their player hears it: the class line in the hero's band for a
 * masculine voice, a battle effort (a laugh, a cry) for a feminine one.
 */
export function playHeroLine(h: { id?: bigint; classId: string; species: number; voice: HeroVoiceCode }, line: HeroLine): void {
  if (h.voice === 0) {
    const key = barkKey(h.classId, line);
    if (key) { playSfx(key, { band: h.id !== undefined ? Number(h.id % 64n) : 0, rate: linePitch(h.species) }); return; }
  }
  const effort: SfxKey = line === 'downed' ? (h.voice === 1 ? 'voice.woman_hurt' : 'voice.man_hurt')
    : line === 'attack' || line === 'ready' ? (h.voice === 1 ? 'voice.woman_attack' : 'voice.man_attack')
      : (h.voice === 1 ? 'voice.woman_taunt' : 'voice.man_taunt');
  playSfx(effort, { rate: effortPitch(h.species) });
}
